/**
 * Secondary Panel Promotion/Demotion Prototype
 * Demonstrates promotion/demotion pattern for secondary panel content
 *
 * Access: http://flownotes.localhost/?test=secondary-panel
 */

import React, { useState, useRef, useEffect } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { MarkdownPreview } from '@/components/features/markdown-preview';
import { 
  ArrowLeft, 
  MessageSquare, 
  FileText, 
  Link2, 
  ChevronRight, 
  X, 
  MoreVertical,
  ExternalLink,
  User,
  PanelRight,
  ArrowRight,
  Maximize2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Types
type SectionType = 'comments' | 'related' | 'work-specs' | null;

interface Comment {
  id: string;
  author: string;
  authorInitials: string;
  content: string;
  date: string;
  time: string;
}

interface RelatedDoc {
  id: string;
  title: string;
  type: 'semantic' | 'casual';
  similarity?: number;
}

interface WorkSpec {
  id: string;
  title: string;
  excerpt: string;
  status: string;
  project: string;
  updatedAt: string;
  tags: string[];
}

// Mock data
const mockComments: Comment[] = [
  {
    id: 'c1',
    author: 'Chris Townsend',
    authorInitials: 'CT',
    content: 'KIA SAID: "Can we download a spreadsheet showing all GGIA practices with their current KEYS TO WELL-BEING tags? We need to tag practices with TOPICS (Workplaces, Parenting, Relationships) and want to see how sorting works. Should we tag in spreadsheet or Expression Engine?"',
    date: 'Oct 7, 2025',
    time: '10:06 AM'
  },
  {
    id: 'c2',
    author: 'Rowan Sentesy',
    authorInitials: 'RS',
    content: 'Hi Kia, Great question! I\'ve attached a spreadsheet showing all GGIA practices with their current Keys To Well-Being tags. The spreadsheet includes:',
    date: 'Oct 7, 2025',
    time: '10:30 AM'
  },
  {
    id: 'c3',
    author: 'Emiliana Simon-Thomas',
    authorInitials: 'ES',
    content: 'Thanks for the spreadsheet! This will help us plan the TOPICS categorization.',
    date: 'Oct 8, 2025',
    time: '2:15 PM'
  }
];

const mockRelated: RelatedDoc[] = [
  { id: 'r1', title: 'Global: Testing & QA (3-4h)', type: 'casual' },
  { id: 'r2', title: 'Basecamp ↔ FlowNotes Integration Ideas', type: 'casual' },
  { id: 'r3', title: '**GGSC Monthly Check-in - November 25, 20...', type: 'casual' },
  { id: 'r4', title: 'Basecamp Todo Ingestion Workflow Documen...', type: 'casual' },
  { id: 'r5', title: 'GGIA', type: 'casual' },
  { id: 'r6', title: 'Project Metadata', type: 'casual' },
  { id: 'r7', title: 'Response to Testing Feedback - Practice Cate...', type: 'casual' },
  { id: 'r8', title: 'Practice Category & Navigation Updates', type: 'semantic', similarity: 0.87 }
];

const mockWorkSpecs: WorkSpec[] = [
  {
    id: 'ws1',
    title: 'Send spreadsheet indicating tagging of Keys To Well-Being for each GGIA practice',
    excerpt: 'Tagging of Keys To Well-Being for each GGIA practice so they can plan TOPICS categorization',
    status: 'active',
    project: 'GGIA',
    updatedAt: '3 days ago',
    tags: ['practice-categories', 'topics', 'domains', 'navigation']
  },
  {
    id: 'ws2',
    title: 'Task Summary',
    excerpt: 'Client needs spreadsheet showing current Keys To Well-Being tags for each GGIA practice',
    status: 'active',
    project: 'Greater Good',
    updatedAt: 'Nov 4',
    tags: ['practice-categories', 'topics', 'domains']
  }
];

// Minimized Section Component
interface MinimizedSectionProps {
  type: SectionType;
  title: string;
  count: number;
  preview?: React.ReactNode;
  onPromote: () => void;
  onDemote: () => void;
  isPromoted: boolean;
}

function MinimizedSection({ type, title, count, preview, onPromote, onDemote, isPromoted }: MinimizedSectionProps) {
  return (
    <div 
      className={`rounded-lg p-3 transition-all duration-500 ease-in-out overflow-hidden ${
        isPromoted 
          ? 'border-l-2 border-l-primary/50 border border-primary/20 bg-primary/10 hover:bg-primary/15 cursor-pointer' 
          : 'border border-border bg-card hover:bg-muted/50'
      }`}
      onClick={isPromoted ? onDemote : undefined}
    >
      <div className="relative min-h-[60px]">
        {/* Promoted state: Just a simplified heading with icon */}
        <div 
          className={`transition-all duration-500 ease-in-out ${
            isPromoted 
              ? 'opacity-100 translate-y-0 pointer-events-auto' 
              : 'opacity-0 -translate-y-4 pointer-events-none absolute inset-0'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PanelRight className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
                {title}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {count}
              </Badge>
            </div>
            <ChevronRight className="h-3 w-3 text-primary rotate-180" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Click to return to left panel
          </p>
        </div>

        {/* Minimized state: Full preview with "View all" button */}
        <div 
          className={`transition-all duration-500 ease-in-out ${
            !isPromoted 
              ? 'opacity-100 translate-y-0 pointer-events-auto' 
              : 'opacity-0 translate-y-4 pointer-events-none absolute inset-0'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {title}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {count}
              </Badge>
            </div>
          </div>
          
          {preview && (
            <div className="mb-2">
              {preview}
            </div>
          )}
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPromote();
            }}
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors mt-2 pt-2 border-t border-border"
          >
            <span>View all {title.toLowerCase()}</span>
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Full Section Components (for right panel)
function CommentsSection({ comments, onDemote }: { comments: Comment[]; onDemote: () => void }) {
  return (
    <div className="flex flex-col h-full bg-muted/30" data-secondary-panel data-panel-type="comments">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Comments</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {comments.length} comment{comments.length !== 1 ? 's' : ''}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={onDemote}>
              Remove from secondary panel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onDemote}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Comments List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {comments.map((comment, index) => (
          <div 
            key={comment.id} 
            className="space-y-2"
            style={{
              animation: `slideDownStagger 0.4s ease-out ${index * 0.05}s both`
            }}
          >
            <div className="flex items-start gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground flex-shrink-0">
                {comment.authorInitials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {comment.author}
                </p>
                <p className="text-xs text-muted-foreground">
                  {comment.date} · {comment.time}
                </p>
              </div>
            </div>

            <div className="space-y-1 pl-10 text-sm text-foreground">
              <p className="whitespace-pre-wrap">{comment.content}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Comment Input */}
      <div className="px-4 py-3 border-t border-border bg-card">
        <textarea
          placeholder="Add a comment..."
          className="w-full h-16 px-3 py-2 text-xs bg-muted rounded-md border border-border text-foreground placeholder-muted-foreground resize-none"
        />
      </div>
    </div>
  );
}

function RelatedSection({ related, onDemote }: { related: RelatedDoc[]; onDemote: () => void }) {
  const [selectedDoc, setSelectedDoc] = useState<RelatedDoc | null>(null);

  const handleDocClick = (doc: RelatedDoc) => {
    setSelectedDoc(doc);
  };

  const handleBackToList = () => {
    setSelectedDoc(null);
  };

  // Mock document content for preview
  const mockDocumentContent = selectedDoc ? `# ${selectedDoc.title}

## Document Details

${selectedDoc.similarity !== undefined 
  ? `**Similarity:** ${(selectedDoc.similarity * 100).toFixed(0)}% similar (semantic match)`
  : '**Type:** Reference document'
}

## Content

This is a mock preview of the related document. In the real implementation, this would load the actual document content from the file system.

The document is related to the current task and may contain relevant information, context, or references.

## Connection Type

${selectedDoc.type === 'semantic' 
  ? 'This document was found through semantic search, meaning it shares conceptual similarities with the current task.'
  : 'This document is a casual reference, likely mentioned or linked in related work.'
}
` : '';

  return (
    <div className="flex flex-col h-full bg-muted/30" data-secondary-panel data-panel-type="related">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between relative">
        <div className="flex items-center gap-2">
          {selectedDoc ? (
            <>
              <button
                onClick={handleBackToList}
                className="text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              >
                Related
              </button>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground truncate max-w-[200px]">
                {selectedDoc.title}
              </span>
            </>
          ) : (
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Related
            </h2>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selectedDoc && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // In real implementation, this would open the document in main viewer
                console.log('Opening related document:', selectedDoc.id);
              }}
              className="h-7 px-2 text-xs"
            >
              Open
              <Maximize2 className="h-3 w-3 ml-1" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onDemote}>
                Remove from secondary panel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={selectedDoc ? handleBackToList : onDemote}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {selectedDoc ? (
        // Preview View
        <div className="flex-1 overflow-y-auto p-6">
          <div 
            style={{
              animation: 'slideDownStagger 0.4s ease-out 0s both'
            }}
          >
            <MarkdownPreview content={mockDocumentContent} className="max-w-none" />
          </div>
        </div>
      ) : (
        // List View
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {related.map((doc, index) => (
            <div
              key={doc.id}
              className="p-3 rounded-lg border border-border bg-card hover:bg-muted cursor-pointer transition-colors group flex items-start justify-between gap-2"
              onClick={() => handleDocClick(doc)}
              style={{
                animation: `slideDownStagger 0.4s ease-out ${index * 0.05}s both`
              }}
            >
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <FileText className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
                  {doc.similarity !== undefined ? (
                    <Badge variant="outline" className="text-xs bg-purple-600/20 text-purple-400 mt-1">
                      {(doc.similarity * 100).toFixed(0)}% similar
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs text-muted-foreground mt-1">
                      Reference
                    </Badge>
                  )}
                </div>
              </div>
              <Button
                variant="secondary"
                size="icon"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Open in editor"
                onClick={(e) => {
                  e.stopPropagation();
                  // In real implementation, this would open in new tab
                  console.log('Opening related document in editor:', doc.id);
                }}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WorkSpecsSection({ workSpecs, onDemote }: { workSpecs: WorkSpec[]; onDemote: () => void }) {
  const [selectedSpec, setSelectedSpec] = useState<WorkSpec | null>(null);

  const handleSpecClick = (spec: WorkSpec) => {
    setSelectedSpec(spec);
  };

  const handleBackToList = () => {
    setSelectedSpec(null);
  };

  // Mock document content for preview
  const mockDocumentContent = selectedSpec ? `# ${selectedSpec.title}

${selectedSpec.excerpt}

## Details

**Project:** ${selectedSpec.project}
**Status:** ${selectedSpec.status}
**Last Updated:** ${selectedSpec.updatedAt}

## Content

This is a mock preview of the work spec document. In the real implementation, this would load the actual document content from the file system.

The document contains details about the task, requirements, and implementation notes.

## Tags

${selectedSpec.tags.map(tag => `- ${tag}`).join('\n')}
` : '';

  return (
    <div className="flex flex-col h-full bg-muted/30" data-secondary-panel data-panel-type="work-specs">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between relative">
        <div className="flex items-center gap-2">
          {selectedSpec ? (
            <>
              <button
                onClick={handleBackToList}
                className="text-sm font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              >
                Work Specs
              </button>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground truncate max-w-[200px]">
                {selectedSpec.title}
              </span>
            </>
          ) : (
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Work Specs
            </h2>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selectedSpec && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                // In real implementation, this would open the document in main viewer
                console.log('Opening work spec:', selectedSpec.id);
              }}
              className="h-7 px-2 text-xs"
            >
              Open
              <Maximize2 className="h-3 w-3 ml-1" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onDemote}>
                Remove from secondary panel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={selectedSpec ? handleBackToList : onDemote}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* Connection line to edge */}
        {selectedSpec && (
          <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
        )}
      </div>

      {selectedSpec ? (
        // Preview View
        <div className="flex-1 overflow-y-auto p-6">
          <div 
            style={{
              animation: 'slideDownStagger 0.4s ease-out 0s both'
            }}
          >
            <MarkdownPreview content={mockDocumentContent} className="max-w-none" />
          </div>
        </div>
      ) : (
        // List View
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {workSpecs.map((spec, index) => (
            <div
              key={spec.id}
              className="w-full p-3 bg-accent/50 rounded-md hover:bg-accent transition-colors border border-border group cursor-pointer"
              onClick={() => handleSpecClick(spec)}
              style={{
                animation: `slideDownStagger 0.4s ease-out ${index * 0.05}s both`
              }}
            >
              <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-medium truncate">{spec.title}</p>
                    {spec.status && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                        {spec.status}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{spec.excerpt}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground">{spec.project}</span>
                    <span className="text-xs text-muted-foreground">· {spec.updatedAt}</span>
                  </div>
                </div>
              </div>
              <Button
                variant="secondary"
                size="icon"
                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Open in editor"
                onClick={(e) => {
                  e.stopPropagation();
                  // In real implementation, this would open in new tab
                  console.log('Opening work spec in editor:', spec.id);
                }}
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </Button>
              </div>
              {spec.tags && spec.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {spec.tags.slice(0, 3).map((tag, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] px-1 py-0 h-4">
                      {tag}
                    </Badge>
                  ))}
                  {spec.tags.length > 3 && (
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                      +{spec.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SecondaryPanelTest() {
  const [promotedSection, setPromotedSection] = useState<SectionType>(null);
  
  // Add CSS animations for staggered slide-down effect
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDownStagger {
        from {
          opacity: 0;
          transform: translateY(-20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style)
    }
  }, []);

  const handlePromote = (section: SectionType) => {
    setPromotedSection(section);
  };

  const handleDemote = () => {
    setPromotedSection(null);
  };

  // Comments preview (last comment)
  const lastComment = mockComments[mockComments.length - 1];
  const commentsPreview = (
    <div className="flex items-start gap-2 text-xs">
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground flex-shrink-0">
        {lastComment.authorInitials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{lastComment.author}</p>
        <p className="text-xs text-muted-foreground line-clamp-1">{lastComment.content}</p>
      </div>
    </div>
  );

  // Related preview (first few items)
  const relatedPreview = (
    <div className="space-y-1">
      {mockRelated.slice(0, 2).map((doc) => (
        <div key={doc.id} className="flex items-center gap-2 text-xs">
          <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground truncate">{doc.title}</span>
        </div>
      ))}
      {mockRelated.length > 2 && (
        <p className="text-xs text-muted-foreground">+{mockRelated.length - 2} more</p>
      )}
    </div>
  );

  // Work specs preview
  const workSpecsPreview = (
    <div className="space-y-1">
      {mockWorkSpecs.slice(0, 1).map((spec) => (
        <div key={spec.id} className="text-xs">
          <p className="text-muted-foreground truncate">{spec.title}</p>
          <div className="flex items-center gap-1 mt-1">
            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
              {spec.status}
            </Badge>
            <span className="text-muted-foreground">{spec.project}</span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b p-4 bg-sidebar">
        <div className="flex items-center gap-3 mb-2">
          <a
            href="/?test=index"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Prototype Index</span>
          </a>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <FileText className="w-6 h-6" />
          <h1 className="text-xl font-bold">Secondary Panel Promotion/Demotion Prototype</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Demonstrates promotion/demotion pattern: minimized sections in left panel can be promoted to right panel for full view
        </p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Click "View all" to promote a section</span>
          <span>·</span>
          <span>Promoted sections show as simplified headings in left panel</span>
          <span>·</span>
          <span>Click simplified heading or X button to demote</span>
        </div>
      </div>

      {/* Content - Split Panel Layout */}
      <PanelGroup direction="horizontal" className="flex-1 overflow-hidden">
        {/* LEFT PANEL - Main Content */}
        <Panel 
          defaultSize={promotedSection ? 60 : 100} 
          minSize={promotedSection ? 40 : 100}
          maxSize={promotedSection ? undefined : 100}
          className="overflow-hidden flex flex-col"
        >
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="px-6 py-8 space-y-8 max-w-3xl">
              {/* Mock Task Header */}
              <div>
                <h1 className="text-2xl font-bold mb-4">
                  Send spreadsheet indicating tagging of Keys To Well-Being for each GGIA practice
                </h1>
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                    Completed
                  </Badge>
                  <Button variant="outline" size="sm">
                    Update Work Spec
                  </Button>
                </div>
              </div>

              {/* Task Metadata */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Due:</span> 10/30/2025
                </div>
                <div>
                  <span className="text-muted-foreground">Created:</span> 10/7/2025
                </div>
                <div>
                  <span className="text-muted-foreground">Assignees:</span> 2
                </div>
                <div>
                  <span className="text-muted-foreground">By:</span> Chris Townsend
                </div>
              </div>

              {/* Minimized Sections */}
              <div className="space-y-4">
                <MinimizedSection
                  type="comments"
                  title="Comments"
                  count={mockComments.length}
                  preview={commentsPreview}
                  onPromote={() => handlePromote('comments')}
                  onDemote={handleDemote}
                  isPromoted={promotedSection === 'comments'}
                />

                <MinimizedSection
                  type="work-specs"
                  title="Work Specs"
                  count={mockWorkSpecs.length}
                  preview={workSpecsPreview}
                  onPromote={() => handlePromote('work-specs')}
                  onDemote={handleDemote}
                  isPromoted={promotedSection === 'work-specs'}
                />

                <MinimizedSection
                  type="related"
                  title="Related"
                  count={mockRelated.length}
                  preview={relatedPreview}
                  onPromote={() => handlePromote('related')}
                  onDemote={handleDemote}
                  isPromoted={promotedSection === 'related'}
                />
              </div>
            </div>
          </div>
        </Panel>

        {/* Resizable Handle */}
        {promotedSection && (
          <>
            <PanelResizeHandle className="w-1 bg-primary/20 hover:bg-primary/30 transition-colors" />

            {/* RIGHT PANEL - Promoted Section */}
            <Panel defaultSize={40} minSize={30} className="overflow-hidden border-l border-primary/20">
              {promotedSection === 'comments' && (
                <CommentsSection comments={mockComments} onDemote={handleDemote} />
              )}
              {promotedSection === 'related' && (
                <RelatedSection related={mockRelated} onDemote={handleDemote} />
              )}
              {promotedSection === 'work-specs' && (
                <WorkSpecsSection workSpecs={mockWorkSpecs} onDemote={handleDemote} />
              )}
            </Panel>
          </>
        )}
      </PanelGroup>
    </div>
  );
}

